import { createSelector } from "reselect";
import { get } from "lodash";
import { getAtIdFromMatchProps } from "../selectorUtils.js";
import { RESOURCE_URI_CHUNKS } from "../../../constants/entity.js";
import {
  DEFAULT_SCHEDULES,
  DEFAULT_SCHEDULE_KEYS
} from "../schedules/configs/defaultSchedulesData.js";
import operationSelector from "../operations/operationSelector.js";

const schedulesDataMapQuery = state => get(state, "schedules.dataMap", null);
const timezonesDataMapQuery = state => get(state, "timezones.dataMap", null);

// -- HELPERS -- //

const scheduleQuery = (state, props, id) => {
  const schedules = get(state, "schedules.dataMap", null);

  if (!schedules) return null;

  const scheduleId = id
    ? id
    : getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.SCHEDULES);

  return schedules[scheduleId];
};

// -- SELECTORS -- //

const selectSchedule = id => {
  return createSelector(
    [scheduleQuery, timezonesDataMapQuery],
    (baseSchedule, timezones) => {
      if (!baseSchedule) return {};

      let schedule = { ...baseSchedule };

      schedule.timezone = timezones[baseSchedule.timezone];

      return schedule;
    }
  );
};

const selectSchedulesDataMap = createSelector(
  [
    schedulesDataMapQuery,
    timezonesDataMapQuery,
    operationSelector.permissionsQuery
  ],
  (schedulesDataMap, timezones, permissions) => {
    if (!schedulesDataMap) return null;

    let schedules = { ...schedulesDataMap };

    // merge timezone objects into schedule returns
    Object.keys(schedules).forEach(key => {
      schedules[key].timezone = timezones[schedules[key].timezone];
    });

    return operationSelector.stampOperationPermissions(schedules, permissions);
  }
);

// for use in any schedule selection dropdowns
const selectSchedulesOptionData = createSelector(
  [schedulesDataMapQuery],
  schedules => {
    let options = [];

    // user schedules
    if (schedules) {
      options = Object.keys(schedules).map(id => {
        const item = schedules[id];

        return {
          key: item["@id"],
          text: item.name,
          value: item["@id"]
        };
      });
    }

    // core schedules - see defaultSchedulesData.js
    options.unshift({
      key: DEFAULT_SCHEDULE_KEYS.OFFICE,
      text: DEFAULT_SCHEDULES[DEFAULT_SCHEDULE_KEYS.OFFICE].description,
      value: DEFAULT_SCHEDULE_KEYS.OFFICE
    });

    return options;
  }
);

const selectScheduleNames = createSelector(
  [schedulesDataMapQuery],
  schedules => {
    let options = {};

    // user schedules
    if (schedules) {
      for (const schedule in schedules) {
        options[schedule["id"]] = schedule.name;
      }
    }

    // core schedules - see defaultSchedulesData.js
    options[DEFAULT_SCHEDULE_KEYS.ALWAYS_OFF] =
      DEFAULT_SCHEDULES[DEFAULT_SCHEDULE_KEYS.ALWAYS_OFF].description;
    options[DEFAULT_SCHEDULE_KEYS.ALWAYS_ON] =
      DEFAULT_SCHEDULES[DEFAULT_SCHEDULE_KEYS.ALWAYS_ON].description;
    options[DEFAULT_SCHEDULE_KEYS.OFFICE] =
      DEFAULT_SCHEDULES[DEFAULT_SCHEDULE_KEYS.OFFICE].description;

    return options;
  }
);

// using the following export pattern so we can mock more easily using jest

const scheduleSelector = {
  selectSchedule,
  selectSchedulesDataMap,
  selectSchedulesOptionData,
  selectScheduleNames
};

export default scheduleSelector;
